import * as uuid from 'uuid';
import _ from 'lodash';
import moment from 'moment';
import { computed, makeObservable, observable } from 'mobx';
import {
	APPLICATION_ID,
	attributes as attributesSymbol,
	displayName as displayNameSymbol,
	fileAttributes,
	modelName as modelNameSymbol,
	references as referencesSymbol,
	serialiseAs,
} from 'Symbols';
import { serialiseDateTime } from 'Util/AttributeUtils';

export type jsonReplacerFn = (input: { [key: string]: any }) => { [key: string]: any };

export interface IModelType<T extends Model = Model> {
	new (attributes?: Partial<IModelAttributes>): T;

	excludeFromCreate: string[];
	excludeFromUpdate: string[];
}

export interface IModelAttributes {
	id?: string;
	created?: Date;
	modified?: Date;
}

/**
 * The target a model decorator is applied to. The decorator metadata (attributes, references, file
 * attributes and serialisers) is stored against symbol keys on the prototype, so the target needs to
 * be indexable by symbol in addition to exposing getModelName.
 * Mirrors the `Record<string | symbol, any>` target type already used by the validator decorators.
 */
type ModelDecoratorTarget = { getModelName: () => string } & Record<string | symbol, any>;

/**
 * Initialises the attributes array on a target model
 * Cannot be run on the abstract model since that will initialise the same array on all child models
 * @param target {T extends Model} The target class to initialise the attributes object on
 */
function initAttributes(target: ModelDecoratorTarget) {
	if (target.getModelName() !== 'Model') {
		if (!target[attributesSymbol]) {
			target[attributesSymbol] = ['id', 'created', 'modified'];
		}
		if (!target[referencesSymbol]) {
			target[referencesSymbol] = [];
		}
		if (!target[fileAttributes]) {
			target[fileAttributes] = [];
		}
		if (!target[serialiseAs]) {
			target[serialiseAs] = [];
		}
	}
}

export type SerialiseFunc<T extends Model, A> = (entity: T, attr: A) => any;

export interface IAttributeOptions<T extends Model, A> {
	isReference?: boolean;
	manyReference?: boolean;
	file?: string;
	/**
	 * How should the attribute be serialised.
	 */
	serialise?: SerialiseFunc<T, A>;
}

/**
 * The attribute decorator for all bot written model properties.
 * This will append to the attribute array and decorate with a mobx observer
 */
export function attribute<T extends Model = Model, A = any>(options?: IAttributeOptions<T, A>) {
	return (target: ModelDecoratorTarget, key: string) => {
		// Init attributes array
		initAttributes(target);

		// Add to the attributes array for any attributes
		if (options?.isReference) {
			target[referencesSymbol].push({
				attribute: key,
				manyReference: options.manyReference,
			});
		} else if (options?.file) {
			target[fileAttributes].push({ name: key, blob: options.file });
		} else {
			target[attributesSymbol].push(key);
		}

		if (options?.serialise) {
			target[serialiseAs].push({ name: key, func: options.serialise });
		}
	};
}

/**
 * @param modelName The name of the model
 * @param displayName The display name of the model for presentation purposes, defaults to the modelName
 */
export function entity(modelName: string, displayName?: string) {
	// Need https://github.com/microsoft/TypeScript/pull/44512 which shall be in typescript 4.4
	return (target: {}) => {
		// The name metadata is stored against symbol keys on the class itself, which is not part of its
		// declared shape, so index through a symbol-keyed view of the target.
		const meta = target as Record<string | symbol, any>;
		meta[modelNameSymbol] = modelName;
		meta[displayNameSymbol] = displayName === undefined ? modelName : displayName;
	};
}

export interface ReferencePath {
	[key: string]: ReferencePath | undefined | null;
}

export class Model implements IModelAttributes {
	public static excludeFromCreate: string[] = [];

	public static excludeFromUpdate: string[] = [];

	/**
	 * Client only id for identifying entities before they are sent up to the server
	 */
	public _clientId = uuid.v4();

	@observable
	public id: string;

	@observable
	private _created: Date;

	@computed
	public get created() {
		return this._created;
	}

	public set created(date: Date) {
		if (typeof date === 'string') {
			this._created = moment.utc(date).toDate();
		} else {
			this._created = date;
		}
	}

	@observable
	private _modified: Date;

	@computed
	public get modified() {
		return this._modified;
	}

	public set modified(date: Date) {
		if (typeof date === 'string') {
			this._modified = moment.utc(date).toDate();
		} else {
			this._modified = date;
		}
	}

	constructor(attributes?: Partial<IModelAttributes>) {
		makeObservable(this);
	}

	public assignAttributes(attributes?: Partial<IModelAttributes>) {
		if (attributes) {
			if (attributes.id !== undefined) {
				this.id = attributes.id;
			}
			if (attributes.created !== undefined) {
				this.created = attributes.created;
			}
			if (attributes.modified !== undefined) {
				this.modified = attributes.modified;
			}
		}
	}

	/**
	 * Turns the model into JSON. Can convert selected submodels to JSON as well using the path param.
	 * @param path A path object for choosing which nested models to convert to JSON
	 * @param excludeCrudFields Should excludeFromCreate and excludeFromUpdate be respected
	 * @param replacer A function to mutate the JSON result
	 * @returns A JSON form of the model
	 * @example
	 * myModel.toJSON();
	 * @example
	 * myModel.toJSON({relation: {subrelation: {}}});
	 */
	public toJSON(
		path: ReferencePath = {},
		excludeCrudFields = false,
		replacer: jsonReplacerFn | undefined = undefined,
	) {
		// Attributes are declared on the concrete subclasses, so the base class has to read them by
		// dynamic key. Index through a single string-keyed view of the instance rather than casting
		// at every access.
		const self = this as unknown as Record<string, any>;

		const json: Record<string, any> = {};
		const pathKeys = Object.keys(path);

		let allKeys = _.uniq(this.attributes.concat(this.references).concat(pathKeys));
		if (excludeCrudFields) {
			const staticType = this.constructor as IModelType;
			const excludeList = this.id ? staticType.excludeFromUpdate : staticType.excludeFromCreate;
			allKeys = allKeys.filter((k: string) => excludeList.indexOf(k) === -1);
		}

		for (const key of allKeys) {
			if (self[key] === null && this.attributes.indexOf(key) !== -1) {
				json[key] = null;
				// eslint-disable-next-line no-continue
				continue;
			}

			const serialiseData = this.serialiseAsData.find(x => x.name === key);
			if (serialiseData) {
				json[key] = serialiseData.func(this, self[key]);
				// eslint-disable-next-line no-continue
				continue;
			}

			switch (typeof self[key]) {
				case 'function':
					// We never want functions
					break;
				case 'object':
					// Format dates as strings
					if (self[key] instanceof Date) {
						json[key] = serialiseDateTime(self[key]);
						break;
					}

					// We only want objects if they are defined in the provided path
					if (pathKeys.indexOf(key) >= 0) {
						if (Array.isArray(self[key])) {
							json[key] = self[key].map((model: Model | { [key: string]: unknown }) => {
								if (typeof model.toJSON === 'function') {
									return model.toJSON(path[key], true);
								}
								return JSON.parse(JSON.stringify(model));
							});
						} else if (self[key] === null) {
							json[key] = null;
						} else if (typeof self[key].toJSON === 'function') {
							json[key] = self[key].toJSON(path[key], true);
						} else {
							json[key] = JSON.parse(JSON.stringify(self[key]));
						}
					}
					break;
				case 'undefined':
					break;
				default:
					if (!key.startsWith('_')) {
						json[key] = self[key];
					}
			}
		}

		for (const file of this.files) {
			const fileBlob = self[file.blob];
			if (fileBlob instanceof Blob) {
				json[file.name] = uuid.v5(`${this._clientId}.${file.name}`, APPLICATION_ID);
			} else if (self[file.name]) {
				json[file.name] = self[file.name];
			}
		}

		if (replacer) {
			return replacer(json);
		}
		return json;
	}

	/*
	 * Gets all attributes for the model
	 */
	public get attributes(): string[] {
		// Written onto the prototype by the @attribute decorator, so not part of the declared shape.
		const meta = this as unknown as Record<string | symbol, any>;
		return [...meta[attributesSymbol]];
	}

	public get references(): string[] {
		const meta = this as unknown as Record<string | symbol, any>;
		return [...meta[referencesSymbol]].map((refData: any) => refData.attribute);
	}

	public get files(): { name: string; blob: string }[] {
		const meta = this as unknown as Record<string | symbol, any>;
		return [...meta[fileAttributes]];
	}

	public get serialiseAsData(): { name: string; func: SerialiseFunc<Model, any> }[] {
		const meta = this as unknown as Record<string | symbol, any>;
		return [...meta[serialiseAs]];
	}

	public getModelName() {
		// The model name metadata is stored on the static instance so access it through the prototype
		return Object.getPrototypeOf(this).constructor[modelNameSymbol];
	}

	public getObject(): Record<string, any> {
		const obj: Record<string, any> = {};

		// Iterate over the instance's own properties
		this.attributes.forEach(key => {
			obj[key] = (this as any)[key];
		});

		return obj;
	}

	public static getObject(instance: any): Record<string, any> {
		const obj: Record<string, any> = {};

		for (const key in instance) {
			if (typeof instance[key] !== 'function') {
				if (typeof instance[key] === 'object') {
					if (instance[key] instanceof Model) {
						obj[key] = instance[key].getObject();
					} else {
						obj[key] = Model.getObject(instance[key]);
					}
				} else {
					obj[key] = instance[key];
				}
			}
		}

		return obj;
	}
}

/**
 * A model viewed as an arbitrarily indexable object.
 *
 * Generic code that receives a `Model` but only knows the attribute name at runtime (validators,
 * generic form fields) needs `model[key]`. The attributes are declared on the concrete subclasses,
 * so `Model` itself has no matching index signature.
 *
 * Prefer this over `any`: it keeps the `Model` members (`toJSON`, `attributes`, `getModelName`, ...)
 * fully typed and still accepts any `Model` subclass, while allowing dynamic-key reads.
 *
 * Do NOT add the index signature to the `Model` class itself — that widens `keyof` for all entity
 * types and breaks the mapped types that key off `keyof TEntity`.
 */
export type IndexableModel = Model & Record<string, any>;
